import { supabase } from '../lib/supabase';
import { logSupabaseQueryError } from './errorLogging';

type NotificationRecord = {
  description: string;
  assigned_to: string;
  company_id: string;
  task_id?: string;
  approval_id?: string;
  open_chat?: boolean;
  message?: string;
  trace_id?: string;
};

const buildTraceId = (prefix: 'asg' | 'cmp'): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const resolveCompanyIdForEmployee = async (employeeId: string): Promise<string | null> => {
  const normalizedEmployeeId = String(employeeId || '').trim();
  if (!normalizedEmployeeId) {
    return null;
  }

  const { data, error } = await supabase
    .from('employees')
    .select('company_id')
    .eq('id', normalizedEmployeeId)
    .maybeSingle();

  if (error) {
    console.warn('Company lookup failed for employee:', normalizedEmployeeId, error.message);
    logSupabaseQueryError(error);
    return null;
  }

  const resolved = String((data as any)?.company_id || '').trim();
  return resolved || null;
};

const getSupabaseFunctionUrl = (functionName: string): string => {
  const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').trim();
  if (!supabaseUrl) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  return `${supabaseUrl}/functions/v1/${functionName}`;
};

const getFunctionHeaders = async (): Promise<Record<string, string>> => {
  const anonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();
  if (!anonKey) {
    throw new Error('Missing VITE_SUPABASE_ANON_KEY');
  }

  const authClient = (supabase as any).auth;
  const session = authClient ? (await authClient.getSession())?.data?.session : null;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: anonKey,
  };

  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }

  return headers;
};

const invokeFunctionViaFetch = async (functionName: string, record: NotificationRecord) => {
  const response = await fetch(getSupabaseFunctionUrl(functionName), {
    method: 'POST',
    headers: await getFunctionHeaders(),
    body: JSON.stringify({ record }),
  });

  let payload: any = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const errorMessage = payload?.error || payload?.message || `HTTP ${response.status}`;
    throw new Error(errorMessage);
  }

  return { data: payload, error: null };
};

const isUnauthorizedError = (message: string): boolean => {
  const lower = String(message || '').toLowerCase();
  return lower.includes('401') || lower.includes('unauthorized');
};

const invokeEdgeFunction = async (functionName: string, record: NotificationRecord) => {
  try {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: { record },
    });

    if (error) {
      if (isUnauthorizedError(String(error.message || ''))) {
        try {
          console.warn(`${functionName} invoke returned 401. Retrying via direct fetch.`);
          return await invokeFunctionViaFetch(functionName, record);
        } catch (fallbackError: any) {
          console.warn(`${functionName} fallback failed:`, fallbackError?.message || fallbackError);
          return null;
        }
      }

      console.warn(`${functionName} invoke failed:`, error.message);
      return null;
    }

    return { data, error: null };
  } catch (error: any) {
    const message = String(error?.message || error || '');

    if (isUnauthorizedError(message)) {
      try {
        console.warn(`${functionName} invoke threw 401. Retrying via direct fetch.`);
        return await invokeFunctionViaFetch(functionName, record);
      } catch (fallbackError: any) {
        console.warn(`${functionName} fallback failed:`, fallbackError?.message || fallbackError);
        return null;
      }
    }

    console.warn(`${functionName} invoke failed:`, message);
    return null;
  }
};

const invokeSendPush = async (record: NotificationRecord) => invokeEdgeFunction('send-push', record);

const insertInAppTaskNotification = async (
  userId: string,
  title: string,
  body: string,
  companyId: string,
  taskId?: string
) => {
  const normalizedUserId = String(userId || '').trim();
  const normalizedCompanyId = String(companyId || '').trim();
  if (!normalizedUserId || !normalizedCompanyId) {
    return;
  }

  const nowIso = new Date().toISOString();
  const { error } = await supabase
    .from('notifications')
    .insert({
      user_id: normalizedUserId,
      title,
      body,
      company_id: normalizedCompanyId,
      entity_type: 'task',
      entity_id: String(taskId || '').trim() || null,
      is_read: false,
      created_at: nowIso,
    });

  if (error) {
    console.warn('Failed to insert task notification row:', error);
    logSupabaseQueryError(error);
  }
};

export const sendApprovalRequestNotification = async (
  approverId: string,
  requesterName: string,
  companyId: string,
  title: string,
  description: string,
  approvalId?: string,
  taskId?: string
): Promise<void> => {
  try {
    const normalizedApproverId = String(approverId || '').trim();
    const normalizedCompanyId = String(companyId || '').trim();
    if (!normalizedApproverId || !normalizedCompanyId) {
      return;
    }

    const safeTitle = String(title || 'Approval Request').trim() || 'Approval Request';
    const safeDescription = String(description || '').trim();
    const safeRequesterName = String(requesterName || 'A teammate').trim() || 'A teammate';
    const body = safeDescription || `${safeRequesterName} sent an approval request: ${safeTitle}`;
    const entityId = String(approvalId || taskId || '').trim() || null;
    const entityType = String(approvalId || '').trim() ? 'approval' : 'task';
    const nowIso = new Date().toISOString();

    const { error: notificationError } = await supabase
      .from('notifications')
      .insert({
        user_id: normalizedApproverId,
        title: safeTitle,
        body,
        company_id: normalizedCompanyId,
        entity_type: entityType,
        entity_id: entityId,
        is_read: false,
        created_at: nowIso,
      });

    if (notificationError) {
      console.warn('Failed to insert approval notification row:', notificationError);
      logSupabaseQueryError(notificationError);
    }

    const record: NotificationRecord = {
      description: body,
      assigned_to: normalizedApproverId,
      company_id: normalizedCompanyId,
      task_id: String(taskId || '').trim() || undefined,
      approval_id: String(approvalId || '').trim() || undefined,
      message: body,
      trace_id: buildTraceId('asg'),
    };

    const pushResult = await invokeSendPush(record);
    if (!pushResult) {
      console.warn('Approval push notification skipped or failed for approver:', normalizedApproverId);
    }
  } catch (error) {
    console.error('Error sending approval request notifications:', error);
    logSupabaseQueryError(error);
  }
};

export const sendTaskAssignmentNotification = async (
  taskDescription: string,
  assignedToName: string,
  assignedBy: string,
  assignedToId: string,
  companyId?: string,
  taskId?: string
): Promise<void> => {
  try {
    const traceId = buildTraceId('asg');
    console.log('Sending task assignment notification...', {
      traceId,
      taskDescription,
      assignedToName,
      assignedBy,
      assignedToId,
      companyId,
    });

    let tenantCompanyId = String(companyId || '').trim();
    if (!tenantCompanyId) {
      tenantCompanyId = (await resolveCompanyIdForEmployee(assignedToId)) || '';
      if (tenantCompanyId) {
        console.log(`[WA-TRACE ${traceId}] Resolved company_id from assignee row:`, tenantCompanyId);
      }
    }

    if (!tenantCompanyId) {
      console.warn(`[WA-TRACE ${traceId}] Missing company_id for assignee. Skipping notifications:`, assignedToId);
      return;
    }

    const record: NotificationRecord = {
      description: taskDescription,
      assigned_to: assignedToId,
      company_id: tenantCompanyId,
      task_id: String(taskId || '').trim() || undefined,
      open_chat: true,
      message: `Task assigned by ${assignedBy}: ${taskDescription}`,
      trace_id: traceId,
    };

    console.log(`[WA-TRACE ${traceId}] Assignment payload:`, JSON.stringify({ record }));

    const pushResult = await invokeSendPush(record);

    if (pushResult) {
      console.log(`[WA-TRACE ${traceId}] Push response:`, JSON.stringify(pushResult.data, null, 2));
    }

    if (!pushResult) {
      console.warn(`[WA-TRACE ${traceId}] Assignment push notification failed. Task is still created.`);
    }
  } catch (error) {
    console.error('Error sending task assignment notifications:', error);
    logSupabaseQueryError(error);
  }
};

export const sendTaskCompletionNotification = async (
  taskDescription: string,
  completedByName: string,
  assignedById: string,
  companyId: string,
  taskId?: string
): Promise<void> => {
  try {
    const targetEmployeeId = String(assignedById || '').trim();
    const tenantCompanyId = String(companyId || '').trim();
    if (!targetEmployeeId) {
      console.warn('Missing assigner id for completion notification. Skipping notification.');
      return;
    }

    if (!tenantCompanyId) {
      console.warn('Missing company_id for completion notification. Skipping notification:', targetEmployeeId);
      return;
    }

    const traceId = buildTraceId('cmp');
    console.log('Sending task completion notification...', {
      traceId,
      taskDescription,
      completedByName,
      assignedById: targetEmployeeId,
      companyId: tenantCompanyId,
    });

    const record: NotificationRecord = {
      description: `Task completed by ${completedByName}: ${taskDescription}`,
      assigned_to: targetEmployeeId,
      company_id: tenantCompanyId,
      task_id: String(taskId || '').trim() || undefined,
      message: `Task completed by ${completedByName}: ${taskDescription}`,
      trace_id: traceId,
    };

    console.log(`[WA-TRACE ${traceId}] Completion payload:`, JSON.stringify({ record }));

    const [pushResult, whatsappResult] = await Promise.all([
      invokeSendPush(record),
      invokeSendWhatsApp(record),
    ]);

    if (pushResult) {
      console.log(`[WA-TRACE ${traceId}] Push response:`, JSON.stringify(pushResult.data, null, 2));
    }

    if (whatsappResult) {
      console.log(`[WA-TRACE ${traceId}] WhatsApp response:`, JSON.stringify(whatsappResult.data, null, 2));
    }

    if (!pushResult && !whatsappResult) {
      console.warn(`[WA-TRACE ${traceId}] Completion notifications failed on all channels. Task update still succeeded.`);
    }
  } catch (error) {
    console.error('Error sending task completion notifications:', error);
    logSupabaseQueryError(error);
  }
};

export const sendTaskMentionNotification = async (
  taskDescription: string,
  senderName: string,
  mentionedEmployeeId: string,
  companyId: string
): Promise<void> => {
  try {
    const tenantCompanyId = String(companyId || '').trim();
    const targetEmployeeId = String(mentionedEmployeeId || '').trim();
    if (!tenantCompanyId || !targetEmployeeId) {
      return;
    }

    const record: NotificationRecord = {
      description: `${senderName} mentioned you in task: ${taskDescription}`,
      assigned_to: targetEmployeeId,
      company_id: tenantCompanyId,
      message: `${senderName} mentioned you in task: ${taskDescription}`,
      trace_id: buildTraceId('asg'),
    };

    const pushResult = await invokeSendPush(record);
    if (!pushResult) {
      console.warn('Mention push notification skipped or failed for employee:', targetEmployeeId);
    }
  } catch (error) {
    console.error('Error sending task mention notification:', error);
    logSupabaseQueryError(error);
  }
};

export const sendTaskCollaborationNotification = async (
  title: string,
  body: string,
  recipientEmployeeId: string,
  companyId: string,
  taskId?: string
): Promise<void> => {
  try {
    const normalizedRecipientId = String(recipientEmployeeId || '').trim();
    const tenantCompanyId = String(companyId || '').trim();
    const safeTitle = String(title || 'Task Update').trim() || 'Task Update';
    const safeBody = String(body || '').trim() || safeTitle;

    if (!normalizedRecipientId || !tenantCompanyId) {
      return;
    }

    await insertInAppTaskNotification(normalizedRecipientId, safeTitle, safeBody, tenantCompanyId, taskId);

    const record: NotificationRecord = {
      description: safeBody,
      assigned_to: normalizedRecipientId,
      company_id: tenantCompanyId,
      task_id: String(taskId || '').trim() || undefined,
      open_chat: true,
      message: safeBody,
      trace_id: buildTraceId('asg'),
    };

    const pushResult = await invokeSendPush(record);
    if (!pushResult) {
      console.warn('Task collaboration push notification skipped or failed for employee:', normalizedRecipientId);
    }
  } catch (error) {
    console.error('Error sending task collaboration notification:', error);
    logSupabaseQueryError(error);
  }
};
